import React, { useState, MouseEvent } from "react";
import { Box, Typography, LinearProgress, Popover } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { StoreCard } from "../../lib/types/detailsTypes";

interface Props {
  card: StoreCard;
}

const PopUpPointCard: React.FC<Props> = ({ card }) => {
  const { balance, maxBalance: goal, rewards = [] } = card;
  const percent = Math.min((balance / goal) * 100, 100);

  const sorted = [...rewards].sort(
    (a, b) => a.balanceRequired - b.balanceRequired
  );

  /* Merge reward list with the final goal */
  if (
    sorted.length === 0 ||
    sorted[sorted.length - 1].balanceRequired !== goal
  ) {
    sorted.push({ balanceRequired: goal, reward: "Final Reward" });
  }

  const rewardTargets = sorted;

  /* Local state for pop-over */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(
    null
  );
  const [rewardText, setRewardText] = useState("");

  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);

  const handleStarClick = (e: MouseEvent<SVGSVGElement>, reward: string) => {
    setAnchorEl(e.currentTarget as SVGSVGElement);
    setRewardText(reward);
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        backgroundColor: card.stampAreaBackgroundColor,
        borderRadius: 1,
        boxShadow: "0 0 4px rgba(0,0,0,.2)",
        color: card.cardTextColor || "#000",
      }}
    >
      {/* Header row */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={600}>
          Points
        </Typography>
        <Typography variant="body2">
          {balance.toLocaleString()} / {goal}
        </Typography>
      </Box>

      {/* Progress bar + star markers */}
      <Box sx={{ position: "relative", mt: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 8,
            borderRadius: 4,
            "& .MuiLinearProgress-bar": {
              backgroundColor: card.activeStampColor || "#4CAF50",
            },
            backgroundColor: card.inactiveStampColor || "#e0e0e0",
          }}
        />

        {rewardTargets.map(({ balanceRequired, reward }, idx) => {
          const pct = (balanceRequired / goal) * 100;
          const achieved = balance >= balanceRequired;
          return (
            <StarIcon
              key={idx}
              onClick={(e) => handleStarClick(e, reward)}
              sx={{
                position: "absolute",
                top: -1,
                left: `calc(${pct}% - 7px)`,
                fontSize: 10,
                color: achieved
                  ? card.cardTextColor || "#000"
                  : card.cardBackgroundColor || "#bdbdbd",
                cursor: "pointer",
                zIndex: 1,
              }}
            />
          );
        })}
      </Box>

      {/* Pop-over for reward text */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2, maxWidth: 240 }}>
          <Typography variant="body2" align="center">
            <strong>Reward:</strong> {rewardText}
          </Typography>
          <Typography variant="body2" align="center">
            {"("}
            {rewardTargets
              .find((r) => r.reward === rewardText)
              ?.balanceRequired.toLocaleString() || "N/A"}{" "}
            {card.cardType === "pointCard" ? "Points" : "Scans"}
            {")"}
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default PopUpPointCard;
